import { useRef, useState } from 'react';
import { Upload, Download, AlertTriangle, FileSpreadsheet, X, Trash2, CheckCircle2 } from 'lucide-react';

import { Card }    from '@/components/ui/Card';
import { Button }  from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { journalImportApi } from '@/api/journalImport.api';
import type { ImportBatch } from '@/api/journalImport.api';
import { JournalImportReviewTable } from '@/components/gl/JournalImportReviewTable';
import { downloadJournalImportTemplate, parseJournalImportFile } from '@/utils/journalImportSheet';

/**
 * Excel upload for journal vouchers (GAP-017).
 *
 * Two states: Upload, and Review. Nothing reaches the ledger from the upload step — the batch is
 * staged, the server validates every row against this company's chart of accounts, and only then
 * can it be posted. Posting runs each balanced group through the same CreateGlJournal command the
 * manual screen uses, so an imported journal is numbered, audited and workflowed exactly like a
 * hand-keyed one. That is the rule this whole feature is built around: no GL row is inserted from
 * the import layer.
 */

type Phase = 'upload' | 'review';

interface Problem { rowNumber: number; message: string }

export const JournalImportPage = () => {
  const { hasPermission } = usePermission();
  const canUpload = hasPermission(Permissions.IMP_JOURNAL_UPLOAD);
  const canPost   = hasPermission(Permissions.IMP_JOURNAL_POST);

  const fileInput = useRef<HTMLInputElement>(null);

  const [phase,   setPhase]   = useState<Phase>('upload');
  const [batch,   setBatch]   = useState<ImportBatch | null>(null);
  const [busy,    setBusy]    = useState<string | null>(null);   // what we are waiting on
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  /** Rows the spreadsheet parser could not read. Distinct from the server's row verdicts. */
  const [problems, setProblems] = useState<Problem[]>([]);

  const readError = (e: unknown): string => {
    const res = (e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
    return res?.data?.errors?.[0]?.message
        ?? res?.data?.message
        ?? (e instanceof Error ? e.message : 'That did not work.');
  };

  const reset = () => {
    setPhase('upload'); setBatch(null); setProblems([]);
    setError(null); setSuccess(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  // ── Upload → stage → validate ───────────────────────────────────────────────
  // Validation runs immediately after staging rather than behind its own button: a staged batch
  // nobody has validated tells the operator nothing, and there is no decision to make in between.
  const handleFile = async (file: File) => {
    setError(null); setSuccess(null); setProblems([]);
    setBusy('Reading the spreadsheet…');
    try {
      const parsed = await parseJournalImportFile(file);
      setProblems(parsed.problems);

      setBusy('Staging the batch…');
      const uploaded = await journalImportApi.upload(file.name, parsed.rows);
      const batchId  = uploaded.data.data?.id;
      if (!batchId) throw new Error('The batch was not created.');

      setBusy('Checking accounts and balances…');
      const validated = await journalImportApi.validate(batchId);
      setBatch(validated.data.data ?? null);
      setPhase('review');
    } catch (e) {
      setError(readError(e));
    } finally {
      setBusy(null);
    }
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  // ── Post ────────────────────────────────────────────────────────────────────
  const handlePost = async () => {
    if (!batch) return;
    setBusy('Posting through the journal engine…'); setError(null);
    try {
      const res    = await journalImportApi.post(batch.id);
      const result = res.data.data;
      setBatch(result ?? null);

      // Posted and submitted are different outcomes and the operator must not read one as the
      // other: on a maker-checker company the journals exist and are waiting for an approver.
      const posted    = result?.postedJournalCount ?? 0;
      const submitted = result?.submittedJournalCount ?? 0;
      setSuccess(
        submitted > 0
          ? `${posted} journal(s) posted, ${submitted} submitted and waiting for approval.`
          : `${posted} journal(s) posted.`);
    } catch (e) {
      setError(readError(e));
    } finally {
      setBusy(null);
    }
  };

  const handleDiscard = async () => {
    if (!batch) return;
    setBusy('Discarding…'); setError(null);
    try {
      await journalImportApi.cancel(batch.id);
      reset();
    } catch (e) {
      setError(readError(e));
    } finally {
      setBusy(null);
    }
  };

  const errorRows   = batch?.rows.filter(r => r.validationStatus === 'INVALID').length ?? 0;
  const alreadyRun  = batch ? batch.status === 'POSTED' || batch.status === 'SUBMITTED' : false;

  // Groups where EVERY row is valid — what would actually post. The batch's own journalCount
  // counts a group with one good line among bad ones, so using it on the button would promise to
  // post journals the server will refuse.
  // (It subtracted groups-with-a-bad-row from groups-with-a-good-row, which went to −1 — "Post −1
  // journals" — whenever every row of a group was invalid.)
  const postableGroups = batch
    ? new Set(batch.rows.map(r => r.journalGroup)).size
      - new Set(batch.rows.filter(r => r.validationStatus !== 'VALID').map(r => r.journalGroup)).size
    : 0;
  // A batch with one bad row is not postable: a group with a bad line is not a journal, and
  // posting "the good ones" would split a document the author meant as one.
  const canPostNow  = Boolean(batch) && canPost && errorRows === 0 && !alreadyRun;

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Import Journals from Excel
          </h1>
          <p className="text-sm text-slate-500 dark:text-gray-400 mt-1 max-w-3xl">
            Upload a spreadsheet of journal lines. Rows sharing a journal reference become one
            voucher. Nothing reaches the ledger until you post — and when you do, each journal goes
            through the same engine, numbering, audit and approval as one keyed by hand.
          </p>
        </div>
        <Button variant="secondary" onClick={downloadJournalImportTemplate}>
          <Download className="w-3.5 h-3.5 mr-1" /> Download template
        </Button>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {success && (
        <div className="flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> {success}
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700">
          <Spinner /> {busy}
        </div>
      )}

      {phase === 'upload' ? (
        <div
          className={`rounded-lg p-10 text-center border-2 border-dashed transition-colors ${
            dragOver
              ? 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10'
              : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'
          }`}
          onDragOver={(e: React.DragEvent) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <Upload className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
            Drop an .xlsx or .csv file here
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400 max-w-md mx-auto">
            The sheet needs Journal, Account Code, Debit and Credit columns. Download the template
            if you are not sure of the layout.
          </p>
          <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                 onChange={onFilePicked} />
          <div className="mt-4">
            <Button disabled={!canUpload || busy !== null} onClick={() => fileInput.current?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1" /> Choose file
            </Button>
          </div>
          {!canUpload && (
            <p className="mt-3 text-xs text-slate-500 dark:text-gray-400">
              You do not have permission to upload journal batches.
            </p>
          )}
        </div>
      ) : batch && (
        <>
          <Card className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-6 text-sm">
                <Stat label="File"     value={batch.fileName ?? '—'} />
                <Stat label="Rows"     value={String(batch.rowCount)} />
                <Stat label="Journals" value={String(postableGroups)} />
                <Stat label="Errors"   value={String(errorRows)}
                      tone={errorRows > 0 ? 'bad' : 'good'} />
                <Stat label="Status"   value={batch.status} />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={reset} disabled={busy !== null}>
                  <X className="w-3.5 h-3.5 mr-1" /> Close
                </Button>
                {!alreadyRun && (
                  <Button variant="secondary" onClick={handleDiscard}
                          disabled={busy !== null || !canUpload}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard batch
                  </Button>
                )}
                {!alreadyRun && (
                  <Button onClick={handlePost} disabled={!canPostNow || busy !== null}>
                    Post {postableGroups} journal{postableGroups === 1 ? '' : 's'}
                  </Button>
                )}
              </div>
            </div>

            {errorRows > 0 && (
              <div className="mt-3 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                {errorRows} row{errorRows === 1 ? '' : 's'} need fixing before this batch can post.
                Correct them in the spreadsheet and upload it again — discard this batch first so
                the same lines are not staged twice.
              </div>
            )}

            {!canPost && errorRows === 0 && !alreadyRun && (
              <p className="mt-3 text-xs text-slate-500 dark:text-gray-400">
                This batch is ready, but posting an import needs the journal-import post permission.
              </p>
            )}
          </Card>

          {problems.length > 0 && (
            <Card className="p-4">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {problems.length} spreadsheet row{problems.length === 1 ? '' : 's'} could not be read
              </h2>
              <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                These were left out of the batch entirely — they are not in the table below.
              </p>
              <ul className="mt-2 space-y-1 text-sm text-amber-800 dark:text-amber-300">
                {problems.map(p => (
                  <li key={p.rowNumber}>Row {p.rowNumber} — {p.message}</li>
                ))}
              </ul>
            </Card>
          )}

          <Card className="overflow-hidden">
            <JournalImportReviewTable batch={batch} />
          </Card>
        </>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) => (
  <div>
    <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
    <div className={
      tone === 'bad'  ? 'font-medium text-rose-700 dark:text-rose-400'
    : tone === 'good' ? 'font-medium text-emerald-700 dark:text-emerald-400'
    :                   'font-medium text-slate-700 dark:text-slate-200'
    }>
      {value}
    </div>
  </div>
);
